import { useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { useUI } from "../providers/MoleUIContext";

/** Record types that carry a backend data_access_scope. */
export type DataScopeResource =
	| "sale"
	| "payment"
	| "pending_deposit"
	| "deposit"
	| "expense"
	| "wallet_record"
	| "voucher"
	| "offer"
	| "supply"
	| "supply_payment"
	| "movement"
	| "discard"
	| "conversion"
	| "production"
	| "inventory";

export type DataScopeKind = "ALL" | "OWN" | "MAPPED";

export type ResolvedDataScope = {
	scope: DataScopeKind;
	/** ANY, TODAY, CURRENT_WEEK, CURRENT_MONTH, CURRENT_YEAR or LAST_DAYS. */
	period: string;
	/** N for LAST_DAYS, otherwise null. */
	days?: number | null;
	/** First visible day (YYYY-MM-DD), or null when unlimited. */
	from: string | null;
};

export type DataScopes = Partial<Record<DataScopeResource, ResolvedDataScope>>;

// One request per access-grant version, shared by every mounted filter.
let cached: { version: number; promise: Promise<DataScopes | null> } | null = null;

/**
 * Resolved read scopes from GET /users/me/data-scopes. The backend already
 * narrows every list; this only lets the UI bound date pickers and explain
 * why a list is narrowed. Null until loaded (treat as unrestricted).
 */
export const useDataScopes = (): DataScopes | null => {
	const { api, accessVersion } = useAuth();
	const { queueReRun } = useUI();
	const [scopes, setScopes] = useState<DataScopes | null>(null);
	const apiRef = useRef(api);
	const queueReRunRef = useRef(queueReRun);
	apiRef.current = api;
	queueReRunRef.current = queueReRun;

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (!cached || cached.version !== accessVersion) {
				const version = accessVersion;
				cached = {
					version,
					promise: apiRef.current.get("/users/me/data-scopes").then((res: any) => {
						if (res?.status === 200) return res.data as DataScopes;
						if (res?.status === 401 || res?.status === 422) {
							cached = null;
							queueReRunRef.current(load);
						}
						return null;
					}),
				};
			}
			const data = await cached.promise;
			if (!cancelled && data) setScopes(data);
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [accessVersion]);

	return scopes;
};

const parseDay = (value: string): Date => {
	const [y, m, d] = value.split("-").map(Number);
	return new Date(y, m - 1, d);
};

export type DataScopeSummary = {
	/** Earliest visible day across the resources, or null when any is unlimited. */
	minDate: Date | null;
	/** Narrowest record scope across the resources. */
	scope: DataScopeKind;
	period: string;
	/** N when the bounding period is LAST_DAYS. */
	days: number | null;
	isRestricted: boolean;
	/**
	 * Set when the view lists several record types whose scopes differ (e.g.
	 * movements limited to 7 days, discards unlimited): each gets its own
	 * caption instead of one merged summary.
	 */
	parts: { resource: DataScopeResource; scope: DataScopeKind; period: string; days: number | null; bounded: boolean }[] | null;
};

/**
 * Combine the scopes of the resources a view shows. A list mixing several
 * (e.g. movements and discards) is only bounded where all of them are.
 */
export const summarizeDataScopes = (
	scopes: DataScopes | null,
	resources: DataScopeResource | DataScopeResource[] | undefined,
): DataScopeSummary | null => {
	if (!scopes || !resources) return null;
	const named = (Array.isArray(resources) ? resources : [resources])
		.map((resource) => ({ resource, value: scopes[resource] }))
		.filter((entry): entry is { resource: DataScopeResource; value: ResolvedDataScope } => !!entry.value);
	const list = named.map((entry) => entry.value);
	if (list.length === 0) return null;

	const froms = list.map((item) => item.from);
	const minDate = froms.some((value) => value === null)
		? null
		: froms.map((value) => parseDay(value as string)).reduce((a, b) => (a < b ? a : b));
	const rank: Record<DataScopeKind, number> = { OWN: 0, MAPPED: 1, ALL: 2 };
	const scope = list.map((item) => item.scope).reduce((a, b) => (rank[a] <= rank[b] ? a : b));
	const bounding = minDate
		? list.find((item) => item.from && parseDay(item.from).getTime() === minDate.getTime())
		: undefined;
	const period = bounding?.period ?? "ANY";
	const days = period === "LAST_DAYS" ? bounding?.days ?? null : null;

	const signature = (item: ResolvedDataScope) => `${item.scope}|${item.period}|${item.days ?? ""}`;
	const differs = named.length > 1 && new Set(list.map(signature)).size > 1;
	const parts = differs
		? named.map(({ resource, value }) => ({
			resource,
			scope: value.scope,
			period: value.period,
			days: value.period === "LAST_DAYS" ? value.days ?? null : null,
			bounded: value.from !== null,
		}))
		: null;

	return { minDate, scope, period, days, isRestricted: scope !== "ALL" || minDate !== null || !!parts, parts };
};
